import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormAnswerMediaFilterInputObject,
  Task,
  TaskCompletion,
} from 'graphql-common';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import TabBar from '@lib/components/TabBar/TabBar';
import { TabBarItem } from '@lib/components/TabBar/constants';
import { ErrorLike } from '@apollo/client';
import Table from '@lib/components/Table/Table';
import { TableProps } from '@lib/components/Table/types';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { ListParams } from '@lib/enums/urls';
import { TaskCompletionsTab } from '@lib/components/TaskCompletions/utils/constants';
import TaskCompletionsMediaGrid from './TaskCompletionsMediaGrid';
import useTaskCompletionsMediaData from './utils/useTaskCompletionsMediaData';
import styles from '../TaskCompletionsModal.module.scss';
import TaskCompletionsHeaderRow from '../TaskCompletionsHeaderRow';
import { TaskCompletionsModalUrls } from '../types';

type Props = {
  task: Task;
  periodLabel: string | null;
  // Narrows the Media tab to the same scope as the completions list.
  formAnswerFilters: FormAnswerMediaFilterInputObject[];
  showTaskChip?: boolean;
  activeTab: TaskCompletionsTab;
  onTabChange: (tab: TaskCompletionsTab) => void;
  tabs: TabBarItem[];
  tableColumns: TableProps<TaskCompletion>['columns'];
  tableData: TaskCompletion[];
  tableLoading: boolean;
  tableFirstLoading: boolean;
  tableError?: ErrorLike;
  totalCount: number;
  paginationParams: ListParams;
  setPaginationParams: React.Dispatch<React.SetStateAction<ListParams>>;
  actionsMenu: TableProps<TaskCompletion>['actionsMenu'];
  actionsForSelected: TableProps<TaskCompletion>['actionsForSelected'];
  hasSelectColumn: boolean;
  useBulkSelectResult: TableProps<TaskCompletion>['useBulkSelectResult'];
  onRowClick: TableProps<TaskCompletion>['onRowClick'];
  urls: TaskCompletionsModalUrls;
};

export default function TaskCompletionsList({
  task,
  periodLabel,
  formAnswerFilters,
  showTaskChip,
  activeTab,
  onTabChange,
  tabs,
  tableColumns,
  tableData,
  tableLoading,
  tableFirstLoading,
  tableError,
  totalCount,
  paginationParams,
  setPaginationParams,
  actionsMenu,
  actionsForSelected,
  hasSelectColumn,
  useBulkSelectResult,
  onRowClick,
  urls,
}: Props) {
  const { t } = useTranslation();
  const isMediaTab = activeTab === TaskCompletionsTab.Media;

  const {
    collection: mediaItems,
    error: mediaError,
    firstLoading: mediaFirstLoading,
    loading: mediaLoading,
    paginationParams: mediaPaginationParams,
    setPaginationParams: setMediaPaginationParams,
    totalCount: mediaTotalCount,
  } = useTaskCompletionsMediaData({
    groupKey: task.groupKey,
    formAnswerFilters,
    skip: !isMediaTab,
  });

  const emptyStateTextByTab: Record<
    TaskCompletionsTab,
    { title: string; text: string }
  > = {
    [TaskCompletionsTab.All]: {
      title: t('no-task-reporting-yet'),
      text: t('no-task-reporting-text'),
    },
    [TaskCompletionsTab.Alert]: {
      title: t('no-tasks-alert-yet'),
      text: t('no-tasks-failure-text'),
    },
    [TaskCompletionsTab.Warning]: {
      title: t('no-tasks-warning-yet'),
      text: t('no-tasks-warning-text'),
    },
    [TaskCompletionsTab.Success]: {
      title: t('no-tasks-success-yet'),
      text: t('no-tasks-success-text'),
    },
    [TaskCompletionsTab.Media]: {
      title: t('no-media-yet'),
      text: t('no-media-text'),
    },
  };

  const emptyState = (
    <ListEmptyState
      title={emptyStateTextByTab[activeTab].title}
      text={emptyStateTextByTab[activeTab].text}
    />
  );

  let leadingHeaderItem: React.ReactNode = null;
  const isDateHeaderItem = !(showTaskChip && task.name) && !!periodLabel;
  if (showTaskChip && task.name) {
    leadingHeaderItem = (
      <Chip
        type={ChipTypes.chipGray}
        size={ChipSizes.small}
        leftIcon="task_alt"
        leftIconSymbolsOutlined
        label={task.name}
      />
    );
  } else if (periodLabel) {
    leadingHeaderItem = (
      <>
        <MaterialIcon icon="calendar_today" symbolsOutlined size="md-16" />
        <Typography variant="body" className={styles.periodText}>
          {periodLabel}
        </Typography>
      </>
    );
  }

  let completionsTableBody: React.ReactElement;
  if (tableError) {
    completionsTableBody = (
      <ErrorPage
        description={getDescriptionFromError(tableError)}
        status={500}
      />
    );
  } else if (tableFirstLoading) {
    completionsTableBody = <CircleLoader />;
  } else {
    completionsTableBody = (
      <Table<TaskCompletion>
        actionsMenu={actionsMenu}
        actionsForSelected={actionsForSelected}
        columns={tableColumns}
        data={tableData}
        emptyState={emptyState}
        hasSelectColumn={hasSelectColumn}
        id="task-completions"
        loading={tableLoading}
        onRowClick={onRowClick}
        onSortChange={(orderingField, orderingDirection) =>
          setPaginationParams((prev) => ({
            ...prev,
            ordering_field: orderingField,
            ordering_direction: orderingDirection,
          }))
        }
        orderingState={[paginationParams, setPaginationParams]}
        paginationState={[paginationParams, setPaginationParams]}
        totalEntries={totalCount}
        useBulkSelectResult={useBulkSelectResult}
      />
    );
  }

  return (
    <div>
      <TaskCompletionsHeaderRow
        task={task}
        leadingItem={leadingHeaderItem}
        isLeadingItemDate={isDateHeaderItem}
        urls={urls}
      />
      <TabBar
        items={tabs}
        active={activeTab}
        onActiveChange={(item) => onTabChange(item.value as TaskCompletionsTab)}
      />
      {isMediaTab ? (
        <TaskCompletionsMediaGrid
          groupKey={task.groupKey}
          formAnswerFilters={formAnswerFilters}
          items={mediaItems}
          loading={mediaLoading}
          firstLoading={mediaFirstLoading}
          error={mediaError}
          totalCount={mediaTotalCount}
          paginationParams={mediaPaginationParams}
          setPaginationParams={setMediaPaginationParams}
        />
      ) : (
        completionsTableBody
      )}
    </div>
  );
}
